import {
    isValidElement,
    useCallback,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef,
    useId
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ICardProps from "./type";
import type {
    CardRegisteredState,
    CardRegisteredPart,
    CardContextType
} from "./type";
import stylesheet, {
    CARD_SURFACE_COLORS,
    getCardInset,
    useStyles
} from "./stylesheet";
import CardAction from "./components/action";
import CardContent from "./components/content";
import CardDescription from "./components/description";
import CardFooter from "./components/footer";
import CardHeader from "./components/header";
import CardTitle from "./components/title";
import {
    CardLayoutContext,
    CardContext
} from "./context";
import {
    getCardLayout
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    warnOnce
} from "../../utils/development";
import StateLayer from "../stateLayer";
import Text from "../text";

const Card: FC<ICardProps> = ({
    spreadBehaviour = "baseline",
    "aria-label": ariaLabel,
    isPressable = false,
    variant = "outline",
    accessibilityLabel,
    isDisabled = false,
    isLoading = false,
    size = "medium",
    customLocalize,
    role: roleProp,
    customTheme,
    onHoverOut,
    onPressOut,
    hrefAttrs,
    onHoverIn,
    onPressIn,
    children,
    onFocus,
    onPress,
    onBlur,
    style,
    href,
    ...props
}) => {
    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        registeredState,
        setRegisteredState
    ] = useState<CardRegisteredState>({});

    const registeredRef = useRef<CardRegisteredState>({});

    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onPressOut: isLoading ? undefined : onPressOut,
        onPressIn: isLoading ? undefined : onPressIn,
        isDisabled,
        onHoverOut,
        onHoverIn,
        onFocus,
        onBlur
    });

    const isWeb = Platform.OS === "web";

    const inset = getCardInset({
        spaces,
        size
    });

    const layout = getCardLayout({
        children
    });

    const currentLabel = ariaLabel ?? accessibilityLabel;

    const currentRole = roleProp ?? (href ? "link" : "button");

    const titleID = `${baseID}-title`;

    const descriptionID = `${baseID}-description`;

    const {
        surface: surfaceDynamicStyle,
        status: statusDynamicStyle,
        layer: layerDynamicStyle
    } = useStyles({
        isFirstFlush: layout.isFirstFlush,
        isLastFlush: layout.isLastFlush,
        spreadBehaviour,
        isFocusVisible,
        shapeConfigs,
        isPressable,
        shadowAlpha,
        elevations,
        isDisabled,
        isLoading,
        radiuses,
        borders,
        variant,
        colors,
        inset
    });

    const registerPart = useCallback((part: CardRegisteredPart, text?: string) => {
        registeredRef.current = {
            ...registeredRef.current,
            [part]: {
                text
            }
        };

        setRegisteredState(registeredRef.current);

        return () => {
            registeredRef.current = {
                ...registeredRef.current,
                [part]: undefined
            };

            setRegisteredState(registeredRef.current);
        };
    }, []);

    useEffect(() => {
        if(!onPress || isPressable) {
            return;
        }

        warnOnce({
            message: "Card: \"onPress\" only works together with \"isPressable\"."
        });
    }, [
        isPressable,
        onPress
    ]);

    useEffect(() => {
        if(!isPressable || isLoading || currentLabel || registeredRef.current.title) {
            return;
        }

        warnOnce({
            message: "Card: a pressable card needs a Card.Title (or an \"aria-label\" / \"accessibilityLabel\") so screen readers can name it."
        });
    }, [
        currentLabel,
        isPressable,
        isLoading
    ]);

    const surfaceColor = CARD_SURFACE_COLORS[variant];

    const context = useMemo<CardContextType>(() => ({
        customLocalize,
        descriptionID,
        registerPart,
        customTheme,
        isPressable,
        isDisabled,
        isLoading,
        titleID,
        inset,
        size
    }), [
        customLocalize,
        descriptionID,
        registerPart,
        customTheme,
        isPressable,
        isDisabled,
        isLoading,
        titleID,
        inset,
        size
    ]);

    const renderPressLayer = () => {
        if(!isPressable) {
            return null;
        }

        const webProps = isWeb ? {
            hrefAttrs,
            href
        } : {};

        const nameProps = isWeb ? {
            "aria-labelledby": !currentLabel && registeredState.title ? titleID : undefined,
            "aria-describedby": registeredState.description ? descriptionID : undefined
        } : {
            accessibilityHint: registeredState.description?.text
        };

        return <Pressable
            {...webProps}
            {...nameProps}
            {...interactionHandlers}
            accessibilityLabel={currentLabel ?? (isWeb ? undefined : registeredState.title?.text)}
            onPress={isLoading ? undefined : onPress}
            aria-disabled={isDisabled}
            style={[
                stylesheet.layer,
                layerDynamicStyle
            ]}
            aria-busy={isLoading}
            disabled={isDisabled}
            role={currentRole}
        >
            <StateLayer
                isHovered={!isLoading && isHovered}
                isPressed={!isLoading && isPressed}
                borderRadius={radiuses.surface}
                borderWidth={borders.line}
                customTheme={customTheme}
                isSquircle={true}
            />
        </Pressable>;
    };

    const renderStatus = () => {
        if(!isLoading) {
            return null;
        }

        return <View
            style={[
                stylesheet.status,
                statusDynamicStyle
            ]}
            aria-live="polite"
            role="status"
        >
            <Text
                customTheme={customTheme}
            >
                {localize("loading")}
            </Text>
        </View>;
    };

    return <CardContext.Provider
        value={context}
    >
        <NCoreUIKitTheme.SurfaceProvider
            surfaceColor={surfaceColor}
        >
            <View
                {...props}
                aria-labelledby={!isPressable && roleProp && !currentLabel && registeredState.title ? titleID : undefined}
                accessibilityLabel={isPressable ? undefined : currentLabel}
                aria-disabled={isDisabled ? true : undefined}
                aria-busy={isLoading || props["aria-busy"]}
                role={isPressable ? undefined : roleProp}
                style={[
                    style,
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                {renderPressLayer()}
                {
                    layout.items.map(({
                        layout: partLayout,
                        node
                    }, index) => <CardLayoutContext.Provider
                        key={isValidElement(node) ? node.key ?? index : index}
                        value={partLayout}
                    >
                        {node}
                    </CardLayoutContext.Provider>)
                }
                {renderStatus()}
            </View>
        </NCoreUIKitTheme.SurfaceProvider>
    </CardContext.Provider>;
};
export default Object.assign(Card, {
    Description: CardDescription,
    Content: CardContent,
    Action: CardAction,
    Footer: CardFooter,
    Header: CardHeader,
    Title: CardTitle
});
